import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel, IconButton  } from '@mui/material';
import styled from '@emotion/styled';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

const CenteredTableCell = styled(TableCell)`
    text-align: center;
    vertical-align: middle;
`;

const StyledTableSortLabel = styled(TableSortLabel)`
    font-size: 1rem;
`;


function GenericTable({ items, columns, filterText, groupRules, getKey = null }) {

    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
    const [collapsedGroups, setCollapsedGroups] = useState({});

    useEffect(() => {
        if (groupRules) {
            const initialCollapsedGroups = {};
            const groupedItems = groupItems(items, groupRules);
            groupedItems.forEach(group => {
                initialCollapsedGroups[group.id] = true;
            });
            setCollapsedGroups(initialCollapsedGroups);
        }
    }, []);

    const getKeyOrFallback = (item) => {
        if (getKey) return getKey(item); // Use provided getKey function
        return item.id; // Fallback logic
    };

    const handleSortChange = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const toggleGroupCollapse = (groupId) => {
        setCollapsedGroups(prevState => ({
            ...prevState,
            [groupId]: !prevState[groupId]
        }));
    };

    const filterItems = (items) => {
        return items.filter(item =>
            columns
                .filter(column => column.filtered)
                .some(column => {
                    // Use filterValue if provided, otherwise fallback to item[column.id]
                    const value = column.filterValue ? column.filterValue(item) : item[column.id];
                    return value?.toString().toLowerCase().includes(filterText.toLowerCase());
                })
        );
    };
    

    const sortItems = (items) => {
        return [...items].sort((a, b) => {
            const column = columns.find(col => col.id === sortConfig.key);
    
            // Use sortValue if provided, otherwise fallback to item[column.id]
            const aValue = column?.sortValue ? column.sortValue(a) : a[sortConfig.key];
            const bValue = column?.sortValue ? column.sortValue(b) : b[sortConfig.key];
    
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    
    const groupItems = (items, groupRules) => {
        const { roleColumn, parentValue, parentColumn, childColumn } = groupRules;
        const groupedItems = {};

        items.forEach(item => {
            if (item[roleColumn] === parentValue) {
                groupedItems[item[parentColumn]] = { ...item, children: [] };
            }
        });

        items.forEach(item => {
            if (groupedItems[item[childColumn]]) {
                groupedItems[item[childColumn]].children.push(item);
            }
        });


        const filteredGroupedItems = [];
        Object.values(groupedItems).forEach(group => {
            group.children = filterItems(group.children);
            if (group.children.length > 0) {
                filteredGroupedItems.push(group);
            }
        });


        const sortedGroupedItems = sortItems(filteredGroupedItems);
        sortedGroupedItems.forEach(group => {
            group.children = sortItems(group.children);
        });

        return sortedGroupedItems;
    };

    const groupedItems = groupRules ? groupItems(items, groupRules) : sortItems(filterItems(items));

    const renderCell = (item, column) => {
        return column.render ? column.render(item) : item[column.id];
    };

    const renderRows = (items) => {
        // return items.map(item => (
        //     <React.Fragment key={item.id}>
        //     <TableRow>
        //         {groupRules && (
        //             <CenteredTableCell>
        //                 {item.children && (
        //                     <IconButton onClick={() => toggleGroupCollapse(item.id)}>
        //                         {collapsedGroups[item.id] ? <ExpandMore /> : <ExpandLess />}
        //                     </IconButton>
        //                 )}
        //             </CenteredTableCell>
        //         )}
        //         {columns.map((column, index) => (
        //             <CenteredTableCell key={column.id} colSpan={index === 0 && groupRules ? 1 : 1}>
        //                 {renderCell(item, column)}
        //             </CenteredTableCell>
        //         ))}
        //     </TableRow>
        //     {!collapsedGroups[item.id] && item.children && renderRows(item.children)}
        //      </React.Fragment>
        // ));
        return items.map(item => {
            const itemKey = getKeyOrFallback(item); // Use dynamic key

            return (
                <React.Fragment key={itemKey}>
                    <TableRow>
                        {groupRules && (
                            <CenteredTableCell>
                                {item.children && (
                                    <IconButton onClick={() => toggleGroupCollapse(itemKey)}>
                                        {collapsedGroups[itemKey] ? <ExpandMore /> : <ExpandLess />}
                                    </IconButton>
                                )}
                            </CenteredTableCell>
                        )}
                        {columns.map((column, index) => (
                            <CenteredTableCell key={column.id} colSpan={index === 0 && groupRules ? 1 : 1}>
                                {renderCell(item, column)}
                            </CenteredTableCell>
                        ))}
                    </TableRow>
                    {!collapsedGroups[itemKey] && item.children && renderRows(item.children)}
                </React.Fragment>
            );
        });
    };


    return (
        <TableContainer component={Paper} style={{ maxHeight: '53vh' }}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        {groupRules && <CenteredTableCell />}
                        {columns.map(column => (
                            <CenteredTableCell key={column.id}>
                                {column.sortable !== false ? (
                                    <StyledTableSortLabel
                                        active={sortConfig.key === column.id}
                                        direction={sortConfig.direction}
                                        onClick={() => handleSortChange(column.id)}
                                        hideSortIcon={true}
                                    >
                                        {column.label}
                                    </StyledTableSortLabel>
                                ) : (
                                    column.label
                                )}
                            </CenteredTableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {renderRows(groupedItems)}
{/* 
                    {sortedItems.map(item => (
                        
                        <TableRow key={item.id}>   
                            {columns.map(column => (
                                <CenteredTableCell key={column.id}>
                                    {renderCell(item, column)}
                                </CenteredTableCell>
                            ))}
                        </TableRow>


                    ))} */}


                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default GenericTable;
